import { t as debounce } from "./app.shared-OQDvbu1B.js";

import { i as onTagalysReady, r as formatResultsCount, t as createProductCardHTML } from "./app.shared-Cz2ME_qv.js";

var search_drawer_default = (() => {
    const FOCUS_RETAKE_MS = 400;
    if (!customElements.get("search-drawer")) customElements.define("search-drawer", class SearchDrawer extends HTMLElement {
        constructor() {
            super();
            this.query = this.dataset.query || "";
            this.checkbox = document.getElementById("SearchDrawer");
            this.input = this.querySelector("[data-search-input]");
            this.clearButton = this.querySelector("[data-clear-search]");
            this.suggestionsView = this.querySelector('[data-view="suggestions"]');
            this.resultsView = this.querySelector('[data-view="results"]');
            this.resultsCount = this.querySelector("[data-results-count]");
            this.viewAllResults = this.querySelector("[data-view-all-results]");
            this.searchUrl = this.viewAllResults?.getAttribute("href") || "/search";
            this.popularSearchesList = this.querySelector("[data-popular-searches-list]");
            this.suggestedProducts = this.querySelector("[data-suggested-products]");
            this.searchProducts = this.querySelector("[data-search-products]");
            this.tabs = Array.from(this.querySelectorAll('[data-search-tabs] [role="tab"]'));
            this.activeTab = this.tabs.find(tab => tab.getAttribute("aria-selected") === "true") || this.tabs[0];
            this.searchInstance = null;
            this.isInitialized = false;
            this.hasLoadedSuggestions = false;
            this.hasLoadedSuggestedProducts = false;
            this.focusRafId = null;
            this.focusRetakeTimer = null;
            this.debouncedOnChange = debounce(this.onChange.bind(this), 300);
        }
        connectedCallback() {
            this.checkbox?.addEventListener("change", this.handleCheckboxChange);
            this.input?.addEventListener("input", this.handleInput);
            this.addEventListener("click", this.handleClick);
            this.addEventListener("keydown", this.handleTabKeydown);
            onTagalysReady(() => this.initializeTagalys());
            if (this.checkbox?.checked) this.handleCheckboxChange();
        }
        disconnectedCallback() {
            this.checkbox?.removeEventListener("change", this.handleCheckboxChange);
            this.input?.removeEventListener("input", this.handleInput);
            this.removeEventListener("click", this.handleClick);
            this.removeEventListener("keydown", this.handleTabKeydown);
            this.cancelFocusRetake();
        }
        handleCheckboxChange=() => {
            if (!this.checkbox.checked) {
                this.cancelFocusRetake();
                this.input?.blur();
                this.clearSearch();
                return;
            }
            this.focusInput();
            this.scheduleFocusRetake();
            this.loadSuggestedQueries();
            this.loadSuggestedProducts();
            if (this.query) {
                if (this.input) this.input.value = this.query;
                if (!this.isInitialized) this.initializeTagalys();
                this.onChange();
            }
            this.toggleClearButton();
        };
        focusInput() {
            this.input?.focus({
                preventScroll: true
            });
        }
        retakeFocus() {
            if (!this.checkbox?.checked) return;
            const active = document.activeElement;
            if (!active || active === document.body || active === document.documentElement || active === this.checkbox) this.focusInput();
        }
        handleFocusTransitionEnd=event => {
            if (event.target !== this || event.propertyName !== "transform") return;
            this.cancelFocusRetake();
            this.retakeFocus();
        };
        scheduleFocusRetake() {
            this.cancelFocusRetake();
            this.focusRafId = requestAnimationFrame(() => {
                this.focusRafId = null;
                if (this.checkbox?.checked && document.activeElement !== this.input) this.focusInput();
            });
            this.addEventListener("transitionend", this.handleFocusTransitionEnd);
            this.focusRetakeTimer = window.setTimeout(() => {
                this.focusRetakeTimer = null;
                this.removeEventListener("transitionend", this.handleFocusTransitionEnd);
                this.retakeFocus();
            }, FOCUS_RETAKE_MS);
        }
        cancelFocusRetake() {
            if (this.focusRafId) {
                cancelAnimationFrame(this.focusRafId);
                this.focusRafId = null;
            }
            if (this.focusRetakeTimer) {
                clearTimeout(this.focusRetakeTimer);
                this.focusRetakeTimer = null;
            }
            this.removeEventListener("transitionend", this.handleFocusTransitionEnd);
        }
        handleInput=event => {
            this.toggleClearButton();
            this.debouncedOnChange(event);
        };
        handleClick=event => {
            const tab = event.target.closest('[role="tab"]');
            if (tab && this.tabs.includes(tab)) {
                event.preventDefault();
                this.activateTab(tab);
                return;
            }
            const suggestion = event.target.closest("[data-suggestion]");
            if (suggestion) {
                event.preventDefault();
                const text = suggestion.textContent.trim();
                if (this.input && text) {
                    this.input.value = text;
                    this.query = text;
                    this.onChange();
                    this.toggleClearButton();
                }
                return;
            }
            if (event.target.closest("[data-clear-search]")) {
                event.preventDefault();
                this.clearSearch();
                this.input?.focus();
            }
        };
        clearSearch() {
            if (!this.input) return;
            this.input.value = "";
            this.query = "";
            this.onChange();
            this.toggleClearButton();
        }
        handleTabKeydown=event => {
            const tab = event.target.closest('[role="tab"]');
            if (!tab || !this.tabs.includes(tab)) return;
            const index = this.tabs.indexOf(tab);
            let nextIndex;
            switch (event.key) {
              case "ArrowRight":
                nextIndex = (index + 1) % this.tabs.length;
                break;

              case "ArrowLeft":
                nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
                break;

              case "Home":
                nextIndex = 0;
                break;

              case "End":
                nextIndex = this.tabs.length - 1;
                break;

              default:
                return;
            }
            event.preventDefault();
            const target = this.tabs[nextIndex];
            target.focus();
            this.activateTab(target);
        };
        activateTab(tab) {
            if (!tab || tab === this.activeTab) return;
            this.tabs.forEach(current => {
                const selected = current === tab;
                current.setAttribute("aria-selected", selected ? "true" : "false");
                current.setAttribute("tabindex", selected ? "0" : "-1");
            });
            this.activeTab = tab;
            this.hasLoadedSuggestedProducts = false;
            this.loadSuggestedProducts();
            if (this.query && this.isInitialized && this.searchInstance) this.searchInstance.helpers.updateQuery(this.query);
        }
        toggleClearButton() {
            if (!this.clearButton || !this.input) return;
            this.clearButton.classList.toggle("hidden", this.input.value.trim().length === 0);
        }
        initializeTagalys() {
            if (this.isInitialized) return;
            try {
                this.searchInstance = window.Tagalys.APIConnector.SearchSuggestions.new({
                    params: {
                        query: this.query,
                        sections: {
                            products: {
                                count: 12,
                                include: [ "total_count", "items" ]
                            }
                        }
                    },
                    beforeAPICall: params => this.scopeParamsByGender(params),
                    onSuccess: response => this.renderSearchResults(response),
                    onFailure: response => {
                        console.warn("Tagalys search suggestions failed:", response);
                    }
                });
                this.isInitialized = true;
            } catch (error) {
                console.warn("Failed to initialize Tagalys search suggestions:", error);
            }
        }
        scopeParamsByGender(params) {
            const gender = this.activeTab?.dataset.gender || "";
            if (params.sections?.products) {
                const values = {
                    men: [ "mens", "unisex" ],
                    women: [ "womens", "unisex" ],
                    kids: [ "kids" ]
                }[gender];
                if (values) params.sections.products.scope = {
                    "p.m.custom.gender": values
                }; else delete params.sections.products.scope;
            }
            return params;
        }
        loadSuggestedQueries() {
            if (this.hasLoadedSuggestions || !this.popularSearchesList) return;
            this.hasLoadedSuggestions = true;
            onTagalysReady(() => {
                if (!this.searchInstance) this.initializeTagalys();
                if (!this.searchInstance) return;
                this.searchInstance.helpers.getPopularSearches(8).then(response => {
                    this.popularSearchesList.innerHTML = response.map(suggestion => `\n                    <li role="option" aria-selected="false">\n                      <a\n                        href="${window.Shopify?.routes?.root || "/"}search?q=${encodeURIComponent(suggestion.query)}"\n                        class="text-xs uppercase tracking-xs leading-md"\n                        tabindex="0"\n                        data-suggestion\n                      >\n                        ${suggestion.query}\n                      </a>\n                    </li>\n                  `).join("");
                });
            });
        }
        loadSuggestedProducts() {
            if (this.hasLoadedSuggestedProducts || !this.suggestedProducts) return;
            const widgetId = this.activeTab?.dataset.widgetId;
            if (!widgetId) {
                console.warn("No Tagalys suggestions widget ID configured for this tab (settings.tagalys_suggestions_widget_id*).");
                return;
            }
            this.hasLoadedSuggestedProducts = true;
            onTagalysReady(() => {
                try {
                    window.Tagalys.APIConnector.Recommendations.call({
                        params: {
                            recommendationId: widgetId,
                            count: 12
                        },
                        onSuccess: response => {
                            if (response.products?.length) this.suggestedProducts.innerHTML = response.products.map(product => createProductCardHTML(product, {
                                mediaLead: "cutout"
                            })).join("");
                        },
                        onFailure: response => {
                            console.warn("Tagalys recommendations failed:", response);
                            this.hasLoadedSuggestedProducts = false;
                        }
                    });
                } catch (error) {
                    console.warn("Failed to load Tagalys recommendations:", error);
                    this.hasLoadedSuggestedProducts = false;
                }
            });
        }
        onChange() {
            const searchTerm = this.input?.value.trim() || "";
            this.query = searchTerm;
            const hasQuery = searchTerm.length > 0;
            this.suggestionsView?.classList.toggle("hidden", hasQuery);
            this.resultsView?.classList.toggle("hidden", !hasQuery);
            this.resultsView?.classList.toggle("flex", hasQuery);
            if (!hasQuery || !this.isInitialized || !this.searchInstance) return;
            this.searchInstance.helpers.updateQuery(searchTerm);
        }
        renderResultsMeta(totalCount) {
            if (this.resultsCount) this.resultsCount.textContent = formatResultsCount(totalCount, this.query, true);
            if (this.viewAllResults) {
                const params = new URLSearchParams({
                    q: this.query,
                    type: "product"
                });
                this.viewAllResults.href = `${this.searchUrl}?${params}`;
            }
        }
        renderSearchResults(response) {
            const products = response.sections?.products;
            if (!this.searchProducts) return;
            this.renderResultsMeta(products?.total_count || 0);
            if (products?.items?.length) {
                this.searchProducts.innerHTML = products.items.map(product => createProductCardHTML(product, {
                    mediaLead: "cutout"
                })).join("");
                return;
            }
            if (this.query) {
                const message = (window.MBF?.strings?.search?.noResults || "").replace("__terms__", this.query);
                this.searchProducts.innerHTML = `\n              <div class="text-scheme-text text-sm col-span-2 desktop:col-span-4 text-center py-10">\n                ${message}\n              </div>\n            `;
            } else this.searchProducts.innerHTML = "";
        }
    });
})();